import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatFCFA } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ImageGallery from "@/components/ImageGallery";
import PreorderModal from "@/components/PreorderModal";
import { toast } from "sonner";
import { trackAddToCart, trackViewContent } from "@/lib/pixel";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [showPreorder, setShowPreorder] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      trackViewContent(r.data);
    });
  }, [id]);

  if (!product) {
    return <div className="text-center text-[#6B6B6B] py-32">Chargement...</div>;
  }

  const availableStock = product.stock;
  const inStock = availableStock > 0;

  const handleAdd = () => {
    addItem(product, null, qty);
    trackAddToCart({
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity: qty,
    });
    toast.success("Produit ajouté au panier");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-[#6B6B6B] hover:text-[#B8860B] mb-8"
        data-testid="product-back-btn"
      >
        ← Retour
      </button>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <ImageGallery photos={product.photos} name={product.name} />

        <div>
          <div className="label-tiny mb-3">
            {product.category} · {product.audience}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
            {product.name}
          </h1>
          {product.is_favorite && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#FFE9A6] to-[#D4AF37] text-[#0A0A0A]" data-testid="favorite-badge-detail">
              <span>★</span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Coup de cœur du mois</span>
            </div>
          )}
          <div className="mt-6 text-2xl lg:text-3xl text-[#B8860B] font-display" data-testid="product-detail-price">
            {formatFCFA(product.price)}
          </div>

          {product.spiritual_description && (
            <div className="mt-8 p-6 border border-[#B8860B]/40">
              <p className="font-citation italic text-[#1A1A1A]/85 leading-relaxed" data-testid="product-spiritual">
                {`"${product.spiritual_description}"`}
              </p>
            </div>
          )}

          <div className="mt-8 space-y-2 text-sm text-[#1A1A1A]/75">
            <div><span className="text-[#6B6B6B] uppercase tracking-widest text-[10px] mr-3">Matière</span> {product.material}</div>
            <div data-testid="product-stock-line">
              <span className="text-[#6B6B6B] uppercase tracking-widest text-[10px] mr-3">Stock</span>
              {inStock ? `${availableStock} disponibles` : "Rupture de stock"}
            </div>
          </div>

          {inStock ? (
            <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex items-center border border-black/10">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-[#B8860B]">−</button>
                <span data-testid="qty-display" className="px-5 min-w-[40px] text-center">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 py-3 text-[#B8860B]">+</button>
              </div>
              <button onClick={handleAdd} className="btn-primary flex-1" data-testid="add-to-cart-btn">
                Ajouter au panier
              </button>
            </div>
          ) : (
            <button
              className="btn-primary w-full mt-10"
              onClick={() => setShowPreorder(true)}
              data-testid="preorder-btn"
            >
              Précommander
            </button>
          )}
        </div>
      </div>

      <PreorderModal open={showPreorder} onClose={() => setShowPreorder(false)} product={product} />
    </div>
  );
}
